/**
 * Dry-run access tester (Slice G1) — check what verdict a key would get for a method + path,
 * using the exact evaluator the gateway filter uses. No rate limit consumed, no state changed.
 *
 * Makes E2E verification a single call instead of log-watching: pick a route, see ALLOW/DENY +
 * reason + whether it would actually block in the current (per-customer) enforcement config.
 */
import React, { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { Button } from './Button';
import { Pill } from './Pill';
import { Card } from './Card';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const inputCls = 'h-9 px-2.5 rounded-v2-sm border border-v2-border-default bg-v2-surface text-[13px] text-v2-ink focus:outline-none focus:border-v2-action';

export function GatewayAccessTester({ keyId }) {
  const { addToast } = useToast();
  const [method, setMethod] = useState('GET');
  const [path, setPath] = useState('/api/v1/workflows');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!path.trim()) { addToast({ tone: 'warning', message: 'Enter a path to test' }); return; }
    setBusy(true);
    try {
      const res = await api.gatewayTestKeyAccess(keyId, method, path.trim());
      setResult(res && res.data ? res.data : res);
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Test failed' });
    } finally {
      setBusy(false);
    }
  };

  const r = result;
  return (
    <Card padding="sm">
      <Card.Body>
        <div className="flex items-center gap-1.5 mb-1">
          <FlaskConical size={13} className="text-v2-action-strong" />
          <div className="text-[12px] font-semibold text-v2-ink">Dry-run access test</div>
        </div>
        <div className="text-[11px] text-v2-ink-muted mb-2">
          What verdict would this key get for a route? Uses the live evaluator — no rate limit
          consumed, nothing changed.
        </div>
        <div className="flex items-center gap-2 mb-2">
          <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)}>
            {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input type="text" className={`${inputCls} flex-1`} value={path}
            onChange={(e) => setPath(e.target.value)} placeholder="/api/v1/workflows/{id}" />
          <Button variant="primary" size="sm" onClick={run} disabled={busy}>
            {busy ? 'Testing…' : 'Test'}
          </Button>
        </div>
        {r && (
          <div className="text-[12px] border border-v2-border-subtle rounded-v2-sm p-2 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Pill tone={r.allowed ? 'success' : 'danger'} size="sm">{r.allowed ? 'ALLOW' : 'DENY'}</Pill>
              {!r.allowed && <span className="font-mono text-[11px] text-v2-ink-muted">{r.reason}</span>}
              {r.wouldBlock
                ? <Pill tone="warning" size="sm">would block now (enforcing)</Pill>
                : <Pill tone="muted" size="sm">observe (not blocked)</Pill>}
            </div>
            <div className="text-[11px] text-v2-ink-muted">
              route: {r.route?.classified
                ? `${r.route.tier} · ${r.route.scope || '(no scope)'} · ${r.route.module || '—'}`
                : 'unclassified (internal / not customer-reachable)'}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default GatewayAccessTester;
